import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Breadcrumbs,
  Link,
  TextField,
  FormHelperText,
  FormControl,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import { Spinner, CustomizedSnackbars } from "../../../components";

// Import API
import api from "../../../helpers/api";

function AddCategory() {
  const navigate = useNavigate();

  // Loading state
  const [isSpinner, setIsSpinner] = useState(false);

  // Form states
  const [categoryName, setCategoryName] = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [description, setDescription] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState({});

  // Alert states
  const [isOpen, setIsOpen] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [message, setMessage] = useState("");

  // Form validation
  const validateCategoryForm = (formData) => {
    const errors = {};

    if (!formData.categoryName || formData.categoryName.trim() === "") {
      errors.categoryName = "Category Name is required";
    }

    if (!formData.categoryCode || formData.categoryCode.trim() === "") {
      errors.categoryCode = "Category Code is required";
    }

    return errors;
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Validate form
    const formErrors = validateCategoryForm({
      categoryName,
      categoryCode,
    });

    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    // Show spinner
    setIsSpinner(true);

    try {
      // Call API to add category
      const res = await api.saveCategory(categoryName);

      if (res.statusCode === 200) {
        showSuccess("Category added successfully");

        // Navigate back to categories list after short delay
        setTimeout(() => {
          navigate("/categories");
        }, 1000);
      } else {
        showError(res.message || "Failed to add category");
      }
    } catch (error) {
      showError(error.message || "An error occurred while adding category");
    } finally {
      setIsSpinner(false);
    }
  };

  // Utility functions
  const showError = (msg) => {
    setIsOpen(true);
    setAlertType("error");
    setMessage(msg);
  };

  const showSuccess = (msg) => {
    setIsOpen(true);
    setAlertType("success");
    setMessage(msg);
  };

  return (
    <>
      <Spinner isShowSpinner={isSpinner} />
      <CustomizedSnackbars
        isOpen={isOpen}
        typeInfo={alertType}
        messageInfo={message}
        handleCloseSnackbar={() => setIsOpen(false)}
      />

      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 1,
          boxShadow: 1,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            mb: 3,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Breadcrumbs separator="›" aria-label="breadcrumb">
              <Link
                color="inherit"
                onClick={() => navigate("/categories")}
                sx={{ cursor: "pointer", textDecoration: "none" }}
              >
                Categories
              </Link>
              <Typography color="text.primary">Add Category</Typography>
            </Breadcrumbs>

            <Typography variant="h6" sx={{ mt: 1 }}>
              Create New Category
            </Typography>
          </Box>
        </Box>

        {/* Form Content */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Category Information
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.categoryName}>
                  <TextField
                    label="Category Name"
                    variant="outlined"
                    value={categoryName}
                    onChange={(e) => {
                      setCategoryName(e.target.value);
                      // Clear error when input changes
                      if (errors.categoryName) {
                        setErrors((prev) => ({
                          ...prev,
                          categoryName: undefined,
                        }));
                      }
                    }}
                    fullWidth
                    required
                    error={!!errors.categoryName}
                  />
                  {errors.categoryName && (
                    <FormHelperText>{errors.categoryName}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.categoryCode}>
                  <TextField
                    label="Category Code"
                    variant="outlined"
                    value={categoryCode}
                    onChange={(e) => {
                      setCategoryCode(e.target.value);
                      // Clear error when input changes
                      if (errors.categoryCode) {
                        setErrors((prev) => ({
                          ...prev,
                          categoryCode: undefined,
                        }));
                      }
                    }}
                    fullWidth
                    required
                    error={!!errors.categoryCode}
                  />
                  {errors.categoryCode && (
                    <FormHelperText>{errors.categoryCode}</FormHelperText>
                  )}
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Description"
                  variant="outlined"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  fullWidth
                  multiline
                  rows={4}
                />
              </Grid>

              {/* <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isActive}
                      onChange={(e) => setIsActive(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Active Category"
                />
              </Grid> */}
            </Grid>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/categories")}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSubmit}
          >
            Save Category
          </Button>
        </Box>
      </Paper>
    </>
  );
}

export default AddCategory;
